import { LowerCasePipe, NgTemplateOutlet } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { TalkingPointComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/talking-point/talking-point.component';
import { IKpiCard } from '@cta-analytics-details/cta-analytics-details-kpis-tile/cta-analytics-details-kpi-card/cta-analytics-details-kpi-card.interface';
import { AnalyticsDetailsTableComponent } from '@cta-analytics-dialog/deliverable-analytics-content/analytics-details-table/analytics-details-table.component';
import { DeliverableFindingsStore } from '@cta-analytics-dialog/deliverable-analytics-content/deliverable-findings.store';
import { TimelineGraphComponent } from '@cta-analytics-dialog/deliverable-analytics-content/graphs/timeline-graph/timeline-graph.component';
import { ITimelineGraphInputData } from '@cta-analytics-dialog/deliverable-analytics-content/graphs/timeline-graph/timeline-graph.interface';
import { EDeliverableType } from '@enums/campaign.enum';
import { ITalkingPointAnalytics } from '@interfaces/deliverable-analytics.interface';
import { TranslateModule } from '@ngx-translate/core';
import { DotsSpinnerComponent } from '@shared/components/dots-spinner/dots-spinner.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { VideoPlayerComponent } from '@shared/components/video-player/video-player.component';
import { AnalyticsDetailsUtilitiesService } from '@utilities/analytics-details-utilities.service';
import { DurationUtilitiesService } from '@utilities/duration-utilities.service';
import { FindingVisualizerService } from '@utilities/finding-visualizer.service';
import { EChartsOption } from 'echarts';
import { MultiMediaPostAnalyticsBaseDirective } from '../multi-media-post-analytics-base/multi-media-post-analytics-base.directive';
import { ITalkingPointsPostAnalyticsTableData } from './talking-points-post-analytics.interface';

@Component({
  imports: [
    NgTemplateOutlet,
    TranslateModule,
    TimelineGraphComponent,
    AnalyticsDetailsTableComponent,
    MatTableModule,
    MatSortModule,
    MatButtonModule,
    VideoPlayerComponent,
    TalkingPointComponent,
    DotsSpinnerComponent,
    IconComponent,
    LowerCasePipe,
    MatMenuModule,
  ],
  providers: [DeliverableFindingsStore],
  selector: 'app-talking-points-post-analytics',
  templateUrl: './talking-points-post-analytics.component.html',
})
export class TalkingPointsPostAnalyticsComponent extends MultiMediaPostAnalyticsBaseDirective<
  ITalkingPointsPostAnalyticsTableData,
  ITalkingPointAnalytics
> {
  public override readonly deliverableType = EDeliverableType.talkingPoint;
  public override readonly displayedColumns: string[] = ['timestamp', 'title', 'actions'];
  public override readonly dataSource: MatTableDataSource<ITalkingPointsPostAnalyticsTableData> =
    new MatTableDataSource();

  protected override readonly currentDeliverable = computed(() => {
    const deliverableId = this.deliverableId();
    const executionAnalytics = this.executionAnalyticsWithFindings();

    if (deliverableId && executionAnalytics) {
      return executionAnalytics?.ctaAnalytics?.talkingPoints?.find((tp) => tp.deliverableId === deliverableId);
    }

    return null;
  });

  public override readonly kpiCardData: IKpiCard = {
    icon: 'view.svg',
    id: 'talkingPoints-count',
    title: this.translate.instant('pages.campaignDetails.ctaAnalytics.details.talkingPoint.totalCount'),
    value: 0,
  };
  public override readonly chartConfiguration = signal<Partial<EChartsOption>>({
    title: {
      text: this.translate.instant('pages.campaignDetails.ctaAnalytics.details.talkingPoint.talkingPointOverTime'),
    },
  });

  private readonly findingVisualizerService = inject(FindingVisualizerService);

  protected override prepareChartAndTableData(): void {
    const dotGraphData: ITimelineGraphInputData[] = [];
    const keywords = this.currentDeliverable()?.keywords ?? [];
    const removedIds = this.manualFindingStatusStore?.serverRemovedIds() ?? new Set<string>();
    const visualizers = this.findingVisualizerService.buildFromKeywords(keywords, removedIds);

    const tableData: ITalkingPointsPostAnalyticsTableData[] = visualizers.map((visualizer) => {
      const [primaryFinding] = visualizer.findings;
      const aggregatedVideoTime = this.calculateAggregatedVideoTime(
        primaryFinding.mediaIndex,
        primaryFinding.videoTime,
      );

      const kwAnalytics = keywords.find((k) => k.keywordId === primaryFinding.parentId);

      dotGraphData.push({
        id: visualizer.id,
        mediaIndex: primaryFinding.mediaIndex,
        status: kwAnalytics?.status ?? primaryFinding.status,
        videoTime: primaryFinding.videoTime,
        xAxisValue: aggregatedVideoTime,
      });

      return {
        ...visualizer,
        aggregatedVideoTime,
        mediaIndex: primaryFinding.mediaIndex ?? 0,
        text: primaryFinding.text,
        timestamp: DurationUtilitiesService.convertSecondsToVideoTime(aggregatedVideoTime, true),
        videoTime: primaryFinding.videoTime ?? 0,
      };
    });

    const sortedTableData = [...tableData].sort((a, b) => a.aggregatedVideoTime - b.aggregatedVideoTime);

    this.dataSource.data = sortedTableData;
    this.dataCount.set(sortedTableData.length);
    this.dotGraphData.set([...dotGraphData].sort((a, b) => a.xAxisValue - b.xAxisValue));
    this.setKeywordTableRowIds(this.findingVisualizerService.buildKeywordRowIdMap(sortedTableData));
  }

  protected override setCounts(): void {
    const talkingPointsAnalytics = this.currentDeliverable();

    const keywords = talkingPointsAnalytics?.keywords ?? [];
    const keywordValues = keywords.map((t) => t.keyword);
    const findings = keywords.flatMap((t) => t.findings ?? []);
    const uniqueFindings = AnalyticsDetailsUtilitiesService.getUniqueTextFindings(findings) as string[];

    const counts = AnalyticsDetailsUtilitiesService.getIndividualKeywordCounts(keywordValues, uniqueFindings);
    const total = Object.values(counts).reduce((sum, val) => sum + val, 0);
    this.kpiCardData.value = total;

    AnalyticsDetailsUtilitiesService.mapKeywordCountsToDeliverableForm(this.deliverableForm(), counts);
  }

  protected override setKeywordList(): void {
    const talkingPointsAnalytics = this.currentDeliverable();

    if (talkingPointsAnalytics?.keywords && talkingPointsAnalytics.keywords.length > 0) {
      const { keywordList, kpiCardList } = AnalyticsDetailsUtilitiesService.buildKeywordLists(
        talkingPointsAnalytics.keywords,
        'keyword',
        'findings',
        'equalizer.svg',
      );
      this.keywordList.set(keywordList);
      this.keywordCardList.set(kpiCardList);
    }
  }

  protected override getKeywords(): string[] {
    return this.currentDeliverable()?.keywords?.map((k) => k.keyword) ?? [];
  }
}
